import React from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  DASHBOARD_COLORS,
  dashboardRowCardSx,
  sectionTitleSx,
  dashboardLinkButtonSx,
  dashboardTableHeadCellSx,
  dashboardTableBodyCellSx,
} from './dashboardStyles';

const LOW_STOCK_LIMIT = 5;

const StockBadge = ({ value, reorder }) => {
  const stock = Number(value) || 0;
  const level = Number(reorder) || 10;
  const critical = stock <= 0 || stock <= 3 || stock < level * 0.5;
  const color = critical ? DASHBOARD_COLORS.danger : DASHBOARD_COLORS.warning;
  const bg = critical ? '#fef2f2' : '#fffbeb';
  const border = critical ? '#fecaca' : '#fde68a';

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 32,
        px: 1.25,
        py: 0.35,
        borderRadius: 1,
        fontWeight: 700,
        fontSize: '0.8125rem',
        color,
        bgcolor: bg,
        border: `1px solid ${border}`,
      }}
    >
      {value}
    </Box>
  );
};

const LowStockTable = ({ items, loading, onViewAll }) => {
  const displayItems = (items || []).slice(0, LOW_STOCK_LIMIT);

  return (
    <Paper elevation={0} sx={dashboardRowCardSx}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography sx={sectionTitleSx}>Low Stock Items</Typography>
        {onViewAll && (
          <Button size="small" onClick={onViewAll} sx={dashboardLinkButtonSx}>
            View All
          </Button>
        )}
      </Box>

      {loading ? (
        <Box flex={1} display="flex" alignItems="center" justifyContent="center" minHeight={180}>
          <CircularProgress size={28} />
        </Box>
      ) : displayItems.length === 0 ? (
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            border: `1px solid ${DASHBOARD_COLORS.border}`,
            borderRadius: 1.5,
            bgcolor: '#fafbfc',
            minHeight: 200,
            px: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: DASHBOARD_COLORS.textMuted, textAlign: 'center' }}>
            All products have enough stock.
          </Typography>
        </Box>
      ) : (
        <TableContainer
          sx={{
            flex: 1,
            border: `1px solid ${DASHBOARD_COLORS.border}`,
            borderRadius: 1.5,
            overflow: 'hidden',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={dashboardTableHeadCellSx}>Product</TableCell>
                <TableCell align="center" sx={dashboardTableHeadCellSx}>
                  Current Stock
                </TableCell>
                <TableCell align="right" sx={dashboardTableHeadCellSx}>
                  Reorder Level
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayItems.map((row, index) => (
                <TableRow
                  key={row.productId || row.productName}
                  sx={{
                    '&:last-child td': { borderBottom: index === displayItems.length - 1 ? 'none' : undefined },
                    '&:hover': { bgcolor: '#f9fafb' },
                  }}
                >
                  <TableCell sx={{ ...dashboardTableBodyCellSx, fontWeight: 500, maxWidth: 0 }}>
                    <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.productName}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={dashboardTableBodyCellSx}>
                    <StockBadge value={row.currentStock} reorder={row.minStockLevel} />
                  </TableCell>
                  <TableCell align="right" sx={{ ...dashboardTableBodyCellSx, fontWeight: 600, color: DASHBOARD_COLORS.textMuted }}>
                    {row.minStockLevel ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default LowStockTable;
